import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import React from 'react'

import ConfirmationDialog from '@/app/component/atom/confirmation-dialog'
import AudioBulkUpload from '@/app/module/campaigns/component/audio-bulk-upload'
import AudioFileTable, { type AudioTableFiles } from '@/app/module/campaigns/component/audio-file-table'
import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { setDidChange } from '@/app/module/campaigns/store/slices/campaign-item.slice'
import { CampaignItemWithVoice } from '@/app/module/campaigns/types'
import { getCampaignAudioReferences } from '@/app/module/campaigns/utils/audio-file-references'
import { useAppDispatch } from '@/app/store/redux-hooks'

type Props = {
  item: CampaignItemWithVoice
}

const AudioFilesContainer: React.FC<Props> = ({ item }) => {
  const { audioFiles: files, removeFile } = useCampaignFiles()
  const dispatch = useAppDispatch()
  const [filenameToRemove, setFilenameToRemove] = React.useState<string>()
  const tableFiles = React.useMemo<AudioTableFiles>(() => {
    const referencedFilenames = getCampaignAudioReferences(item)
    return Object.fromEntries(
      Object.entries(files || {}).map(([filename, file]) => [
        filename,
        { ...file, removable: !referencedFilenames.has(file.filename) },
      ]),
    )
  }, [files, item])
  const toBeRemoved = filenameToRemove ? tableFiles[filenameToRemove] : undefined
  const hasFiles = files && Object.keys(files).length > 0
  const isUnsaved = !('id' in item)

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <CardHeader title="Audio Files" style={{ padding: '12px 24px' }} />
      <Box px={3} pt={2}>
        <AudioBulkUpload />
      </Box>

      <CardContent>
        {!hasFiles && !isUnsaved && (
          <Typography data-testid="audio-files-empty" variant="body1">
            No audio files in this campaign.
          </Typography>
        )}
        {isUnsaved && (
          <Typography data-testid="audio-files-unsaved" variant="body1" color="text.secondary" gutterBottom>
            Save the campaign first to start uploading and managing audio files. Once saved, any audio you upload will
            be linked to this campaign and available for reuse.
          </Typography>
        )}
        {hasFiles && <AudioFileTable files={tableFiles} onRemove={setFilenameToRemove} />}
      </CardContent>
      {toBeRemoved?.removable && (
        <ConfirmationDialog
          deleteButtonText="Remove"
          isOpen
          text={`Remove "${toBeRemoved.title || toBeRemoved.filename}" from this campaign?`}
          title="Remove audio file"
          onClose={() => setFilenameToRemove(undefined)}
          onConfirm={() => {
            removeFile(toBeRemoved.filename)
            dispatch(setDidChange(true))
            setFilenameToRemove(undefined)
          }}
        />
      )}
    </Card>
  )
}

export default AudioFilesContainer
